import { clsx, type ClassValue } from 'clsx'
import { twMerge } from 'tailwind-merge'

export function cn(...inputs: ClassValue[]) {
  return twMerge(clsx(inputs))
}

/**
 * Password validation utilities
 */
export interface PasswordValidation {
  hasNumber: boolean
  hasSpecialChar: boolean
  hasUppercase: boolean
  hasLowercase: boolean
  isValid: boolean
}

export function validatePassword(password: string): PasswordValidation {
  const hasNumber = /\d/.test(password)
  const hasSpecialChar = /[!@#$%^&*()_+\-=\[\]{};':"\\|,.<>\/?]/.test(password)
  const hasUppercase = /[A-Z]/.test(password)
  const hasLowercase = /[a-z]/.test(password)

  return {
    hasNumber,
    hasSpecialChar,
    hasUppercase,
    hasLowercase,
    isValid: hasNumber && hasSpecialChar && hasUppercase && hasLowercase,
  }
}

/**
 * Parses a markdown-formatted JSON string to a JSON object
 * Handles strings wrapped in ```json ... ``` code blocks
 * @param markdownJson - The markdown-formatted JSON string
 * @returns Parsed JSON object
 *
 * @example
 * ```typescript
 * const markdownString = `\`\`\`json
 * {
 *   "visual_journey": "My eyes are drawn immediately to the front wheel...",
 *   "emotional_landscape": "I feel a warm, nostalgic sense...",
 *   "strategic_refinements": "A slight shift in the camera angle...",
 *   "score": 7.9
 * }
 * \`\`\``
 *
 * const parsed = parseMarkdownJson(markdownString)
 * console.log(parsed.visual_journey) // "My eyes are drawn immediately to the front wheel..."
 * console.log(parsed.score) // 7.9
 * ```
 */
export function parseMarkdownJson(markdownJson: string): any {
  try {
    // Remove markdown code block formatting
    let jsonString = markdownJson.trim()

    // Remove ```json and ``` from the beginning and end
    if (jsonString.startsWith('```json')) {
      jsonString = jsonString.substring(7) // Remove ```json
    } else if (jsonString.startsWith('```')) {
      jsonString = jsonString.substring(3) // Remove ```
    }

    if (jsonString.endsWith('```')) {
      jsonString = jsonString.substring(0, jsonString.length - 3) // Remove ```
    }

    // Trim whitespace and parse as JSON
    const cleanedJson = jsonString.trim()
    return JSON.parse(cleanedJson)
  } catch (error) {
    console.error('Error parsing markdown JSON:', error)
    throw new Error(
      `Failed to parse markdown JSON: ${error instanceof Error ? error.message : 'Unknown error'}`,
    )
  }
}

/**
 * Type-safe version of parseMarkdownJson with generic type
 * @param markdownJson - The markdown-formatted JSON string
 * @returns Parsed JSON object of type T
 */
export function parseMarkdownJsonTyped<T>(markdownJson: string): T {
  return parseMarkdownJson(markdownJson) as T
}
